import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { Link } from 'react-router-dom';

import { distances, border, colors } from '../../../styles/constants';
import { DateTime, Money } from '../../../components/Formatters';
import Card from '../../../components/Card';
import Hr from '../../../components/Hr';
import { H3 } from '../../../components/Typography';
import { DraftOrder, ApiEvent, LineItem } from '../../../types/orders';
import { groupItems } from '../../helpers';

import CreatedBy from '../../common/CreatedBy';

const PATHS = {
    DRAFT_ORDERS: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/draft_orders\/?$/,
    DRAFT_ORDERS_ID: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/draft_orders\/[a-zA-Z0-9_\-.]+\/?$/,
    DRAFT_ORDERS_ID_COMPLETE: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/draft_orders\/[a-zA-Z0-9_\-.]+\/complete\/?$/,
    DRAFT_ORDERS_ID_ITEMS: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/draft_orders\/[a-zA-Z0-9_\-.]+\/items\/?$/,
    DRAFT_ORDERS_ID_ITEMS_ID: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/draft_orders\/[a-zA-Z0-9_\-.]+\/items\/[a-zA-Z0-9_\-.]+\/?$/,
};

interface Request {
    path?: string;
    method?: string;
    body?: any;
}

const safeJsonParse = (body: string | undefined) => {
    try {
        return body ? JSON.parse(body) : undefined;
    } catch {
        console.log('Failed JSON.parse of', body);
        return null;
    }
};

const getRequest = (event: ApiEvent): Request => {
    const { path, method, body } = event?.request || {};
    return {
        path,
        method,
        body: safeJsonParse(body),
    };
};

const matches = (method: string, path: RegExp, request: Request) => {
    const chopped = (request.path || '').split('?')[0];
    return method === request.method && path.test(chopped);
};

const getStatusTitle = (t: any, event: ApiEvent) => {
    const request = getRequest(event);

    if (matches('POST', PATHS.DRAFT_ORDERS, request)) {
        return t('orders.draft_order.history.events.created');
    }
    if (matches('PUT', PATHS.DRAFT_ORDERS_ID, request)) {
        return t('orders.draft_order.history.events.updated');
    }
    if (matches('PUT', PATHS.DRAFT_ORDERS_ID_COMPLETE, request)) {
        return t('orders.draft_order.history.events.completed');
    }
    if (matches('POST', PATHS.DRAFT_ORDERS_ID_ITEMS, request)) {
        return t('orders.draft_order.history.events.item_added');
    }
    if (matches('PUT', PATHS.DRAFT_ORDERS_ID_ITEMS_ID, request)) {
        return t('orders.draft_order.history.events.item_updated');
    }
    if (matches('DELETE', PATHS.DRAFT_ORDERS_ID_ITEMS_ID, request)) {
        return t('orders.draft_order.history.events.item_removed');
    }
    return event.version || event.id;
};

const getLastPathId = (path: string | undefined) => {
    const chopped = (path || '').split('?')[0];
    return chopped
        .split('/')
        .reverse()
        .find((x) => x);
};

const Item = (
    {
        item,
        draft,
    }: {
        item: LineItem;
        draft: DraftOrder;
    }
) => {
    return (
        <ItemField>
            <span>
                {[item.quantity && `${item.quantity} * `, item.description, item.description_alias]
                    .filter((x) => x)
                    .join(', ')}
            </span>
            {item.gross_amount !== undefined && <Money amount={item.gross_amount} currency={draft.order.currency} />}
        </ItemField>
    );
};

const FullDraftSummary = (
    {
        draft,
    }: {
        draft: DraftOrder;
    }
) => {
    const items = groupItems(draft.order.items || []);
    return (
        <>
            {items.map((item, i) => (
                <Item item={item} draft={draft} key={`${item.line_id}----${i}`} />
            ))}
        </>
    );
};

const EventSummary = (
    {
        event,
        draft,
        t,
    }: {
        event: ApiEvent;
        t: any;
        draft: DraftOrder;
    }
) => {
    const request = getRequest(event);

    if (matches('POST', PATHS.DRAFT_ORDERS, request) || matches('PUT', PATHS.DRAFT_ORDERS_ID, request)) {
        const draft = request.body as DraftOrder;
        return <FullDraftSummary {...{ draft, t }} />;
    }
    if (matches('POST', PATHS.DRAFT_ORDERS_ID_ITEMS, request)) {
        const item = request.body as LineItem;
        return <Item item={item} draft={draft} />;
    }
    if (matches('PUT', PATHS.DRAFT_ORDERS_ID_ITEMS_ID, request)) {
        const item = request.body as LineItem;

        return (
            <>
                <Field>{`${t('orders.fields.line_item_id')} ${getLastPathId(request.path)}`}</Field>
                <Item item={item} draft={draft} />
            </>
        );
    }
    if (matches('DELETE', PATHS.DRAFT_ORDERS_ID_ITEMS_ID, request)) {
        return <>{`${t('orders.fields.line_item_id')} ${getLastPathId(request.path)}`}</>;
    }

    const orderLink = (draft._links || []).find(link => link.rel === 'order');
    if (matches('PUT', PATHS.DRAFT_ORDERS_ID_COMPLETE, request) && orderLink) {
        const orderInternalId = orderLink.href.split('/').pop();
        if (orderInternalId) {
            return <Link to={`../orders/${orderInternalId}`}>{t('orders.draft_order.history.link_to_order')}</Link>;

        }
    }
    return null;
};

export interface DraftOrderHistoryProps extends WithTranslation {
    draft: DraftOrder;
}



const DraftOrderHistory = (
    {
        t,
        draft,
    }: DraftOrderHistoryProps
) => {
    const events = draft.events || [];
    const deleted = draft.deleted_by !== undefined;
    return (
        <Card marginBottom={distances.normal}>
            <H3>{t('orders.draft_order.history.title')}</H3>
            <Hr />
            <div>
                {events.map((event, i) => {
                    const summary = EventSummary({ t, event, draft });

                    return (
                        <EventWrapper key={i}>
                            <Dot className={i === events.length - 1 && !deleted ? 'active dot' : 'dot'} />
                            <H3>{getStatusTitle(t, event)} </H3>
                            <DateTimeWrapper>
                                <DateTime isoString={event.created_at || ''} boldTime />{' '}
                                <By>
                                    {t('orders.draft_order.history.created_by')} <CreatedBy createdBy={event.created_by} />
                                </By>
                            </DateTimeWrapper>
                            {summary && <Field>{summary}</Field>}
                        </EventWrapper>
                    );
                })}
                {deleted && (
                    <EventWrapper key={draft.deleted_by}>
                        <Dot className={'active dot'} />
                        <H3>{t('orders.draft_order.deleted')}</H3>
                        <DateTimeWrapper>
                            <DateTime isoString={draft.deleted_at || ''} boldTime />{' '}
                            <By>
                                {t('orders.draft_order.history.created_by')} <CreatedBy createdBy={draft.deleted_by} />
                            </By>
                        </DateTimeWrapper>
                    </EventWrapper>
                )}
            </div>
        </Card>
    );
};

export default withTranslation()(DraftOrderHistory);



const By = styled.span`
    display: inline-block;
    margin-left: ${distances.small};
`;

const Dot = styled.div`
    position: absolute;
    top: ${distances.tiny};
    left: -6px;
    height: ${distances.small};
    width: ${distances.small};
    border-radius: ${distances.small};
    background: rgba(242, 242, 242, 1);

    &:after {
                    content: ' ';
        position: absolute;
        top: 4px;
        left: 4px;
        height: ${distances.tiny};
        width: ${distances.tiny};
        border-radius: ${distances.tiny};
        background: #46535e;
        opacity: 0;
    }
    &.active:after {
                    opacity: 1;
    }
`;

const EventWrapper = styled.div`
    padding-top: 0;
    padding-left: ${distances.small};
    padding-bottom: 22px;
    width: 100%;
    font-size: 11px;
    position: relative;
    color: #46535e;

    &:before {
                    content: ' ';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
    &:first-child:before {
                    content: ' ';
        position: absolute;
        top: ${distances.small};
        left: 0;
        bottom: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
    &:first-child:last-child:before {
                    display: none;
    }

    &:last-child:before {
                    content: ' ';
        position: absolute;
        top: 0;
        height: 10px;
        left: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
`;

const Field = styled.span`
    display: block;
    word-break: normal;
    white-space: nowrap;
`;

const ItemField = styled.span`
    display: flex;
    justify-content: space-between;
    word-break: normal;
    white-space: nowrap;
    border-bottom: 1px dashed transparent;

    &:hover {
        border-bottom: 1px dashed #cecece;
    }
`;

const DateTimeWrapper = styled.div`
    font-weight: 400;
    line-height: ${distances.small};
    font-feature-settings: 'tnum';
`;
